/**
 * Which AI provider and model each workspace runs on.
 *
 * Laid out like /admin/ai/credits, which solves the same problem: a global default
 * plus per-workspace overrides. A master who has used one screen already knows
 * this one, and the row states which of the two it is showing rather than
 * leaving it to be inferred from whether a field looks filled in.
 *
 * @module pages/admin/AiModels
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Cpu, Loader2 } from "lucide-react";
import { YDMPageHero } from "@/components/brand";
import {
  clearModel,
  fetchCatalog,
  fetchSelections,
  setModel,
  fetchWorkspaceRows,
  type CatalogEntry,
  type ModelSelection,
  type WorkspaceRow,
} from "@/services/aiModelService";
import { ModelField } from "@/components/admin/ModelField";
import { isAllowedModel } from "@/config/geminiAllowlist";

const BUILT_IN = { provider: "gemini", model: "gemini-2.5-flash" };

/**
 * A saved Gemini id the chat routes now refuse, so every chat that runs on it
 * fails. Only marked: the row stays as saved until a master changes it.
 */
const refusedNote = (selection: ModelSelection | null): string | null =>
  selection && selection.provider === "gemini" && !isAllowedModel(selection.model)
    ? `แชทไม่รับ ${selection.model} ที่บันทึกไว้ — แชทที่ใช้ค่านี้จะใช้งานไม่ได้`
    : null;

export const AdminAiModels: React.FC = () => {
  const [catalog, setCatalog] = useState<CatalogEntry[]>([]);
  const [selections, setSelections] = useState<ModelSelection[]>([]);
  const [workspaces, setWorkspaces] = useState<WorkspaceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [cat, sel, ws] = await Promise.all([fetchCatalog(), fetchSelections(), fetchWorkspaceRows()]);
    setCatalog(cat);
    setSelections(sel);
    setWorkspaces(ws);
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const globalSelection = useMemo(
    () => selections.find((s) => s.workspaceId === null) ?? null,
    [selections],
  );
  const effectiveDefault = globalSelection ?? { ...BUILT_IN, workspaceId: null, updatedAt: "" };

  const save = async (
    workspaceId: string | null,
    provider: string,
    model: string,
    verifiedOk: boolean | null,
  ): Promise<void> => {
    setBusy(workspaceId ?? "global");
    setError(null);
    const result = await setModel(workspaceId, provider, model, verifiedOk);
    if (!result.ok) setError(result.error ?? "บันทึกไม่สำเร็จ");
    await load();
    setBusy(null);
  };

  const clear = async (workspaceId: string): Promise<void> => {
    setBusy(workspaceId);
    setError(null);
    const result = await clearModel(workspaceId);
    if (!result.ok) setError(result.error ?? "ลบไม่สำเร็จ");
    await load();
    setBusy(null);
  };

  // Suggestions only — the field accepts anything well-formed, except a Gemini
  // id the chat routes refuse, which it will not save, so none is offered.
  const suggestions = catalog
    .filter((c) => c.provider !== "gemini" || isAllowedModel(c.model))
    .map((c) => c.model);
  const globalNote = refusedNote(globalSelection);

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-6 text-sm text-neutral-700">
        <Loader2 size={16} className="animate-spin" />
        กำลังโหลด…
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <YDMPageHero eyebrow="SUPER ADMIN" headline="AI MODELS" subtitle="เลือก provider และ model ต่อ workspace" />

      {error && (
        <p role="alert" className="rounded-lg border border-error-200 bg-error-50 p-3 text-sm text-error-800">
          {error}
        </p>
      )}

      <section className="rounded-lg border border-neutral-200 bg-white p-4 space-y-2">
        <h2 className="flex items-center gap-1.5 text-base font-semibold text-ink-900">
          <Cpu size={16} className="text-neutral-700" />
          ค่าเริ่มต้น
        </h2>
        <p className="text-sm text-neutral-700">
          ใช้กับทุก workspace ที่ไม่ได้ตั้งค่าแยก
          {!globalSelection && (
            <>
              {" "}
              — ตอนนี้ยังไม่ได้ตั้ง จึงใช้ค่าที่ฝังไว้ในระบบ (<code className="text-xs">{BUILT_IN.model}</code>)
            </>
          )}
        </p>
        <ModelField
          provider={effectiveDefault.provider}
          model={effectiveDefault.model}
          suggestions={suggestions}
          verifiedOk={globalSelection?.verifiedOk ?? null}
          busy={busy !== null}
          saveLabel="บันทึกค่าเริ่มต้น"
          onSave={(p, m, v) => void save(null, p, m, v)}
        />
        {globalNote && <p className="text-xs text-error-700">{globalNote}</p>}
      </section>

      <section className="rounded-lg border border-neutral-200 bg-white p-4 space-y-3">
        <h2 className="text-base font-semibold text-ink-900">ตั้งค่าแยกตาม workspace</h2>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[34rem] text-sm">
            <thead className="text-xs uppercase tracking-wide text-neutral-700">
              <tr>
                <th className="py-1.5 pr-3 text-left font-medium">Workspace</th>
                <th className="py-1.5 pr-3 text-left font-medium">Model</th>
                <th className="py-1.5 text-left font-medium">ที่มา</th>
              </tr>
            </thead>
            <tbody>
              {workspaces.map((ws) => {
                const own = selections.find((s) => s.workspaceId === ws.id) ?? null;
                // Own rows only: a row that inherits is covered by the default's note.
                const note = refusedNote(own);
                return (
                  <tr key={ws.id} className="border-t border-neutral-100">
                    <td className="py-2 pr-3 font-medium text-ink-900">{ws.name}</td>
                    <td className="py-2 pr-3">
                      <ModelField
                        provider={own?.provider ?? effectiveDefault.provider}
                        model={own?.model ?? effectiveDefault.model}
                        suggestions={suggestions}
                        verifiedOk={own?.verifiedOk ?? null}
                        busy={busy !== null}
                        saveLabel={own ? "บันทึก" : "ตั้งเฉพาะ workspace นี้"}
                        onSave={(p, m, v) => void save(ws.id, p, m, v)}
                        onClear={own ? () => void clear(ws.id) : undefined}
                      />
                      {note && <p className="mt-2 text-xs text-error-700">{note}</p>}
                    </td>
                    {/* Say which of the two a row is showing. A picker holding
                        the same value as the default is ambiguous otherwise:
                        inherited and pinned-to-the-same look identical. */}
                    <td className="py-2 text-xs text-neutral-700">
                      {own ? "ตั้งเฉพาะ workspace นี้" : "สืบทอดจากค่าเริ่มต้น"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <p className="text-xs text-neutral-700">
        พิมพ์ชื่อ model ได้เอง เพราะผู้ให้บริการเปลี่ยนรุ่นบ่อยกว่าที่เราจะตามแก้รายการไหว —
        รายการที่ขึ้นมาให้เลือกเป็นเพียงคำแนะนำ <strong>กด “ทดสอบ” ก่อนบันทึกเสมอ</strong>:
        ชื่อ Claude หรือ GPT ที่พิมพ์ผิดจะบันทึกผ่านตามปกติ แล้วไปพังตอนผู้ใช้ถาม ไม่ใช่ตอนนี้
        ส่วน Gemini รุ่นที่แชทไม่รับจะบันทึกไม่ได้
        · Claude และ GPT ใช้ได้เมื่อ server มีคีย์ (ANTHROPIC_API_KEY / OPENAI_API_KEY) · แชทรับ Gemini เฉพาะบางรุ่น — ปุ่มทดสอบจะบอกทั้งสองกรณี
      </p>
    </div>
  );
};

export default AdminAiModels;
